import { useState, useEffect } from 'react';
import { postRequest } from '../../utils/networking';
import { apiUrls } from '../../utils/url';
import { toast } from 'react-toastify';
import { Search, X, Loader2, Users, Download } from 'lucide-react';
import Pagination from '../../components/Pagination';
import CommonToastContainer from '../../components/CommonToastContainer';
import NoDataFound from '../../components/NoDataFound';
import { utils, writeFile } from 'xlsx';


export default function WebinarRegistration() {
    const [registrations, setRegistrations] = useState([]);
    const [webinars, setWebinars] = useState([]);
    const [selectedWebinarId, setSelectedWebinarId] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);


    const fetchWebinars = async () => {
        try {
            const response = await postRequest(apiUrls.getcurrentwebinar);
            if (response.status === 'S' && response.data) {
                setWebinars(Array.isArray(response.data) ? response.data : []);
            }
        } catch (err) {
            console.error('Error fetching webinars:', err);
        }
    };

    const fetchRegistrations = async (webinarid = 0) => {
        setIsLoading(true);
        try {
            const response = await postRequest(apiUrls.getwebinarregistration, {
                webinarid: Number(webinarid)
            });
            console.log('Webinar registrations response:', response);

            if (response.status === 'S' && response.data) {
                let registrationData = [];
                if (Array.isArray(response.data)) {
                    registrationData = response.data;
                } else if (response.data.registrations && Array.isArray(response.data.registrations)) {
                    registrationData = response.data.registrations;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    registrationData = response.data.data;
                }
                
                console.log('Processed registrations:', registrationData);
                setRegistrations(registrationData);
            } else {
                setRegistrations([]);
            }
        } catch (err) {
            console.error('Error fetching webinar registrations:', err);
            toast.error('Unable to load registrations. Please try again later.');
            setRegistrations([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchWebinars();
        fetchRegistrations(0);
    }, []);

    const handleWebinarChange = (e) => {
        const id = Number(e.target.value);
        setSelectedWebinarId(id);
        fetchRegistrations(id);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
    };

    const filteredRegistrations = registrations.filter(reg => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            (reg.name && reg.name.toLowerCase().includes(searchLower)) ||
            (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
            (reg.phoneNumber && reg.phoneNumber.toLowerCase().includes(searchLower)) ||
            (reg.webinarname && reg.webinarname.toLowerCase().includes(searchLower))
        );
    });

    const totalItems = filteredRegistrations.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentRegistrations = filteredRegistrations.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };



    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleDownloadExcel = () => {
        if (filteredRegistrations.length === 0) {
            toast.info("No registrations available to download.");
            return;
        }

        try {
            const dataToExport = filteredRegistrations.map((reg, index) => ({
                'S.No': index + 1,
                'Name': reg.username || reg.name || '-',
                'Email': reg.email || '-',
                'Phone': reg.phoneno || reg.phoneNumber || '-',
                'WhatsApp': reg.phoneno || reg.phoneNumber || '-',
                'Webinar Name': reg.webinarname || reg.title || '-',
                'Registration Date': formatDate(reg.createdon || reg.registrationDate)
            }));

            const worksheet = utils.json_to_sheet(dataToExport);
            const workbook = utils.book_new();
            utils.book_append_sheet(workbook, worksheet, 'Registrations');

            let fileName = 'Webinar_Registrations.xlsx';
            if (selectedWebinarId !== 0) {
                const selectedWebinar = webinars.find(w => (w.webinarid || w.id) === selectedWebinarId);
                const webinarName = selectedWebinar ? (selectedWebinar.webinarname || selectedWebinar.title) : '';
                if (webinarName) {
                    const cleanName = webinarName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
                    fileName = `Webinar_Registrations_${cleanName}.xlsx`;
                }
            }

            writeFile(workbook, fileName);
            toast.success("Excel download started successfully!");
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            toast.error("Failed to export registrations to Excel.");
        }
    };

    return (
        <div className="bg-[#F8F9FE] text-zinc-900 antialiased h-full overflow-hidden">
            <main className="max-w-[1400px] mx-auto h-full flex flex-col px-4 py-6 w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                    {/* Search Bar - Left */}
                    <div className="relative w-full sm:w-80">
                        <input
                            type="text"
                            placeholder="Search by name, email, or webinar..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm transition-all"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        {searchTerm && (
                            <button
                                onClick={clearSearch}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Webinar Filter - Right */}
                    <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                        <select
                            value={selectedWebinarId}
                            onChange={handleWebinarChange}
                            className="w-full sm:w-64 px-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white shadow-sm transition-all"
                        >
                            <option value={0}>All Webinars</option>
                            {webinars.map(webinar => (
                                <option key={webinar.webinarid || webinar.id} value={webinar.webinarid || webinar.id}>
                                    {webinar.webinarname || webinar.title}
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={handleDownloadExcel}
                            disabled={filteredRegistrations.length === 0}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl shadow-sm hover:shadow hover:cursor-pointer transition-all duration-200"
                            title="Download registrations as Excel sheet"
                        >
                            <Download className="w-4 h-4" />
                            <span>Download</span>
                        </button>
                    </div>
                </div>

                <div className="bg-white/70 rounded-xl border border-zinc-100 shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden">
                    <div className="overflow-auto flex-1 flex flex-col min-h-0">
                        <div className="min-w-[1000px] flex-1 flex flex-col min-h-0">
                            {/* Header */}
                            <div className="sticky top-0 z-10 grid grid-cols-[60px_1.5fr_2fr_1.2fr_1.2fr_1.5fr_140px] gap-4 px-6 py-4 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200 text-sm font-semibold text-zinc-600 capitalize">
                                <div className="flex items-center justify-center">S.No</div>
                                <div className="flex items-center">Name</div>
                                <div className="flex items-center">Email</div>
                                <div className="flex items-center justify-center">Phone</div>
                                <div className="flex items-center justify-center">WhatsApp</div>
                                <div className="flex items-center">Webinar Name</div>
                                <div className="flex items-center justify-center">Date</div>
                            </div>

                            {/* Body */}
                            <div className="flex-1 relative min-h-[400px]">
                                {isLoading ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                                        <div className="text-center">
                                            <Loader2 className="h-10 w-10 animate-spin text-amber-500 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">Loading registrations...</p>
                                        </div>
                                    </div>
                                ) : currentRegistrations.length > 0 ? (
                                    currentRegistrations.map((reg, index) => (
                                        <div
                                            key={reg.id || index}
                                            className="grid grid-cols-[60px_1.5fr_2fr_1.2fr_1.2fr_1.5fr_140px] gap-4 py-4 px-6 border-b border-zinc-100 hover:bg-zinc-50 transition-colors cursor-default"
                                        >
                                            <div className="text-sm font-semibold text-zinc-800 flex items-center justify-center">
                                                {startIndex + index + 1}
                                            </div>
                                            <div className="text-sm font-medium text-zinc-900 flex items-center truncate" title={reg.username || reg.name}>
                                                {reg.username || reg.name || '-'}
                                            </div>
                                            <div className="text-sm text-zinc-600 flex items-center truncate" title={reg.email}>
                                                {reg.email || '-'}
                                            </div>
                                            <div className="text-sm text-zinc-700 font-mono flex items-center justify-center">
                                                {reg.phoneno || reg.phoneNumber || '-'}
                                            </div>
                                            <div className="text-sm flex items-center justify-center">
                                               {reg.phoneno || reg.phoneNumber || '-'}
                                            </div>
                                            <div className="text-sm font-medium text-zinc-900 flex items-center truncate" title={reg.webinarname || reg.title}>
                                                {reg.webinarname || reg.title || '-'}
                                            </div>
                                            <div className="text-sm text-zinc-600 flex items-center justify-center font-medium">
                                                {formatDate(reg.createdon || reg.registrationDate)}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <NoDataFound searchTerm={searchTerm} />
                                )}
                            </div>
                        </div>
                    </div>

                    {totalItems > 0 && !isLoading && (
                        <div className="mt-auto border-t border-zinc-100 bg-white/50">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                itemsPerPage={itemsPerPage}
                                totalItems={totalItems}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </div>
                    )}
                </div>
            </main>
            


            <CommonToastContainer />
        </div>
    );
}
